'use client';

/**
 * CoinBalance Component
 *
 * Displays the user's current gold coin balance with React Query caching.
 * Balance is cached client-side and only refetched after spending/purchasing coins.
 * Clicking opens the buy coins modal — or routes to /pricing (premium) while
 * coin purchases are hidden (COIN_PURCHASES_ENABLED=false).
 */
import { useState } from 'react';

import { useRouter } from 'next/navigation';

import { Coins, Loader2, Plus } from 'lucide-react';

import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';

import { COIN_PURCHASES_ENABLED } from '~/config/monetization.config';

import { BuyCoinsModal } from './buy-coins-modal';
import { useCoinBalance, useInvalidateCoinBalance } from './use-coin-balance';

interface CoinBalanceProps {
  /**
   * Display variant
   * - 'compact': Just the number and icon
   * - 'full': Shows "Gold Coins" label
   */
  variant?: 'compact' | 'full';

  /**
   * Show the buy button
   */
  showBuyButton?: boolean;

  /**
   * Custom class name
   */
  className?: string;
}

export function CoinBalance({
  variant = 'compact',
  showBuyButton = true,
  className = '',
}: CoinBalanceProps) {
  const { data, isLoading } = useCoinBalance();
  const invalidateBalance = useInvalidateCoinBalance();
  const [showBuyModal, setShowBuyModal] = useState(false);
  const router = useRouter();

  const handleBuyClick = () =>
    COIN_PURCHASES_ENABLED ? setShowBuyModal(true) : router.push('/pricing');

  const balance = data?.coins ?? 0;

  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className={`flex items-center gap-1.5 sm:gap-2 ${className}`}>
        {/* Balance Display */}
        <button
          onClick={handleBuyClick}
          className="flex items-center gap-1 rounded-full border border-yellow-500/30 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 px-2 py-1.5 transition-colors hover:border-yellow-500/50 sm:gap-1.5 sm:px-3"
        >
          <Coins className="h-3.5 w-3.5 flex-shrink-0 text-yellow-500 sm:h-4 sm:w-4" />
          <span className="text-xs font-semibold text-yellow-500 sm:text-sm">
            {balance.toLocaleString()}
          </span>
          {variant === 'full' && (
            <span className="ml-1 hidden text-xs text-yellow-500/70 sm:inline">
              <Trans i18nKey="common:gold" />
            </span>
          )}
        </button>

        {/* Buy Button */}
        {showBuyButton && (
          <Button
            variant="outline"
            size="icon"
            className="border-eros-pink/30 hover:border-eros-pink hover:bg-eros-pink/10 h-7 w-7 flex-shrink-0 rounded-full sm:h-8 sm:w-8"
            onClick={handleBuyClick}
          >
            <Plus className="text-eros-pink h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </Button>
        )}
      </div>

      {/* Buy Coins Modal — not mounted while pack purchases are hidden */}
      {COIN_PURCHASES_ENABLED && (
        <BuyCoinsModal
          open={showBuyModal}
          onOpenChange={setShowBuyModal}
          onPurchaseComplete={invalidateBalance}
        />
      )}
    </>
  );
}

/**
 * Compact coin display for use in tight spaces
 */
export function CoinBalanceCompact({ className = '' }: { className?: string }) {
  return (
    <CoinBalance
      variant="compact"
      showBuyButton={false}
      className={className}
    />
  );
}

// Re-export hooks for external use
export {
  useCoinBalance,
  useInvalidateCoinBalance,
  useCoinCount,
} from './use-coin-balance';
